import { useCallback, useEffect, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import NewMessagesContainer from 'components/NewMessagesContainer';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import { fetchUltimosAtendimentosDoContato } from 'store/reducers/atendimentos';
import {
  AtualizarMidiaMensagemInterface,
  atualizarMidiaMensagensFinalizadas,
  BaixandoMidiaMensagemInterface,
  baixandoMidiaMensagensFinalizadas,
  fetchMensagensFinalizadasByAtendimento,
  mensagensSelector,
  navegarParaMensagem,
  resetHistoricoCursorAConsumir,
  resetIdMensagemPesquisada,
  resetMensagensFinalizadas,
  setCursorAConsumir,
  setBeforeCursorAConsumir,
  setAfterCursorAConsumir,
} from 'store/reducers/mensagens';

type TabPanelHistoricoProps = {
  atendimentoId?: string;
  contatoId?: string;
  channelCode?: string;
  initialCursor?: string;
  searchQuery?: string;
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'hidden',
    height: '100%',
  },
}));

export default function TabPanelHistorico(props: TabPanelHistoricoProps) {
  const { atendimentoId, contatoId, channelCode, initialCursor, searchQuery } =
    props;
  const [loadedAtendimentoId, setLoadedAtendimentoId] = useState<
    string | undefined
  >(atendimentoId);
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [initialLoadingMessages, setInitialLoadingMessages] = useState(false);

  const {
    historicoCursorAConsumir,
    mensagensFinalizadas,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
    loadingMensagensFinalizadas,
  } = useAppSelector(mensagensSelector);

  const loadNextMessages = useCallback(() => {
    const { afterCursor: afterAConsumir } = historicoCursorAConsumir[0] || {};
    const { afterCursor } = mensagensFinalizadasCursor;

    const id = atendimentoId ?? loadedAtendimentoId;

    if (id) {
      if (mensagemIdPesquisada) {
        if (afterAConsumir) {
          dispatch(
            fetchMensagensFinalizadasByAtendimento({
              atendimentoId: id,
              cursor: { afterCursor: afterAConsumir },
              apenasMsgsDesteAtendimento: false,
            }),
          )
            .unwrap()
            .then(data => {
              const { afterCursor } = data.cursor;

              dispatch(
                setAfterCursorAConsumir({ afterCursor, type: 'historico' }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensFinalizadasByAtendimento({
            atendimentoId: id,
            cursor: { afterCursor },
            apenasMsgsDesteAtendimento: false,
          }),
        )
          .unwrap()
          .then(data => {
            const { afterCursor } = data.cursor;

            dispatch(
              setAfterCursorAConsumir({ afterCursor, type: 'historico' }),
            );
          });
      }
    }
  }, [
    atendimentoId,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
    loadedAtendimentoId,
  ]);

  const loadPreviousMessages = useCallback(() => {
    const { beforeCursor: beforeAConsumir } = historicoCursorAConsumir[0] || {};
    const { beforeCursor } = mensagensFinalizadasCursor;

    const id = atendimentoId ?? loadedAtendimentoId;

    if (id) {
      if (mensagemIdPesquisada) {
        if (beforeAConsumir) {
          dispatch(
            fetchMensagensFinalizadasByAtendimento({
              atendimentoId: id,
              cursor: { beforeCursor: beforeAConsumir },
              apenasMsgsDesteAtendimento: false,
            }),
          )
            .unwrap()
            .then(data => {
              const { beforeCursor } = data.cursor;

              dispatch(
                setBeforeCursorAConsumir({ beforeCursor, type: 'historico' }),
              );
            });
        }
      } else {
        dispatch(
          fetchMensagensFinalizadasByAtendimento({
            atendimentoId: id,
            cursor: { beforeCursor },
            apenasMsgsDesteAtendimento: false,
          }),
        )
          .unwrap()
          .then(data => {
            const { beforeCursor } = data.cursor;

            dispatch(
              setBeforeCursorAConsumir({ beforeCursor, type: 'historico' }),
            );
          });
      }
    }
  }, [
    atendimentoId,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
    loadedAtendimentoId,
  ]);

  const handleResetLista = useCallback(() => {
    const id = atendimentoId ?? loadedAtendimentoId;

    if (id) {
      setInitialLoadingMessages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetHistoricoCursorAConsumir());
      dispatch(resetMensagensFinalizadas());
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId: id,
          apenasMsgsDesteAtendimento: false,
        }),
      )
        .unwrap()
        .then(data => {
          const { cursor } = data;
          dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
        })
        .finally(() => {
          setInitialLoadingMessages(false);
        });
    }
  }, [atendimentoId, dispatch, loadedAtendimentoId]);

  // Normal Init Effect
  useEffect(() => {
    if (!initialCursor) {
      setInitialLoadingMessages(true);
      dispatch(resetIdMensagemPesquisada());
      dispatch(resetMensagensFinalizadas());
      dispatch(resetHistoricoCursorAConsumir());
      setLoadedAtendimentoId(atendimentoId);

      if (atendimentoId) {
        dispatch(
          fetchMensagensFinalizadasByAtendimento({
            atendimentoId,
            apenasMsgsDesteAtendimento: false,
          }),
        )
          .unwrap()
          .then(data => {
            const { cursor } = data;
            dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
          })
          .finally(() => {
            setInitialLoadingMessages(false);
          });
      } else if (contatoId) {
        dispatch(
          fetchUltimosAtendimentosDoContato({
            status: null,
            contatoId,
            channelCodes: [channelCode ?? ''],
          }),
        )
          .unwrap()
          .then(data => {
            const [lastAtendimento] = data.data;

            if (lastAtendimento) {
              setLoadedAtendimentoId(lastAtendimento.id);
              dispatch(
                fetchMensagensFinalizadasByAtendimento({
                  atendimentoId: lastAtendimento.id,
                  apenasMsgsDesteAtendimento: false,
                }),
              )
                .unwrap()
                .then(data => {
                  const { cursor } = data;
                  dispatch(
                    setCursorAConsumir({ ...cursor, type: 'historico' }),
                  );
                })
                .finally(() => {
                  setInitialLoadingMessages(false);
                });
            } else {
              setInitialLoadingMessages(false);
            }
          });
      }
    }
  }, [atendimentoId, channelCode, contatoId, dispatch, initialCursor]);

  // Search Navigation Effect
  useEffect(() => {
    const id = atendimentoId ?? loadedAtendimentoId;
    if (mensagemIdPesquisada && id) {
      const isAlreadyMountedResult = mensagensFinalizadas.some(
        m => m.id === mensagemIdPesquisada,
      );

      if (!isAlreadyMountedResult) {
        setInitialLoadingMessages(true);
        dispatch(resetHistoricoCursorAConsumir());
        dispatch(resetMensagensFinalizadas());

        dispatch(navegarParaMensagem(mensagemIdPesquisada))
          .unwrap()
          .then(cursorData => {
            const { afterCursor } = cursorData.data;

            dispatch(
              fetchMensagensFinalizadasByAtendimento({
                atendimentoId: id,
                cursor: { afterCursor },
                apenasMsgsDesteAtendimento: false,
              }),
            )
              .unwrap()
              .then(data => {
                const { cursor } = data;
                dispatch(setCursorAConsumir({ ...cursor, type: 'historico' }));
              })
              .finally(() => {
                setInitialLoadingMessages(false);
              });
          });
      }
    }
  }, [
    mensagemIdPesquisada,
    mensagensFinalizadas,
    atendimentoId,
    loadedAtendimentoId,
    dispatch,
  ]);

  useEffect(() => {
    const onBaixandoMidiaMensagem = (data: BaixandoMidiaMensagemInterface) => {
      dispatch(baixandoMidiaMensagensFinalizadas(data));
    };

    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      dispatch(atualizarMidiaMensagensFinalizadas(data));
    };

    socket.on(socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM, onBaixandoMidiaMensagem);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [dispatch]);

  return (
    <Box className={classes.root}>
      {!initialLoadingMessages && mensagensFinalizadas.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="200px"
        >
          <Typography variant="body2" color="textSecondary">
            Nenhum atendimento encontrado para este canal
          </Typography>
        </Box>
      ) : (
        !initialLoadingMessages && (
          <NewMessagesContainer
            messages={mensagensFinalizadas}
            next={loadNextMessages}
            previous={loadPreviousMessages}
            hasAfterCursor={
              mensagemIdPesquisada
                ? Boolean(historicoCursorAConsumir[0]?.afterCursor)
                : Boolean(mensagensFinalizadasCursor.afterCursor)
            }
            hasBeforeCursor={
              mensagemIdPesquisada
                ? Boolean(historicoCursorAConsumir[0]?.beforeCursor)
                : Boolean(mensagensFinalizadasCursor.beforeCursor)
            }
            loading={loadingMensagensFinalizadas}
            scrollableTargetId="tabPanelConversa"
            onResetLista={handleResetLista}
            actions={false}
            historicoAtendimento
            searchQuery={searchQuery}
          />
        )
      )}
    </Box>
  );
}
